import { useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Layout, useUserStore } from "@roc/feature-app-core";
import { Button, DataGrid, GridColumnProps, LinkColumn, Tabs, Toolbar } from "@roc/ui";
import { observer } from "mobx-react";
import { filterProps, formatCurrency, noOpComparator } from "@roc/ui/utils";
import { useExternalAgentLoansStore } from "../../hooks/useExternalAgentLoansStore";
import { useExternalAgentRoutes } from "@roc/feature-title-agent";
import { CounterPartyExpensesTab } from "libs/feature-special-servicing/src/specialServicing/types/types";
import { Box, Grid } from "@material-ui/core";
import { useLoanRoutes } from "@roc/feature-loans-routes-config";

const useStyles = makeStyles(() => ({
  container: {
    maxWidth: "1400px",
  },
  paper: {
    boxShadow: "none !important",
    border: "1px solid #cdcdcd",
    padding: "0px",
    borderRadius: "0 !important",
  },
  gridContainer: {
    display: "flex",
    flexGrow: 1,
    flexDirection: "column",
    height: "65vh",
    overflow: "hidden",
  },
  exportButton: {
    marginRight: '20px'
  },
}));

export const InvoicesDashboard = observer(() => {
  const classes = useStyles();
  const { invoicesDashboardStore, invoicesDashboardStore: { globalStore } } = useExternalAgentLoansStore();
  const { isAttorneyPortal } = globalStore;
  const { externalAgentLoanRoutesConfig } = useExternalAgentRoutes();
  const { userStore } = useUserStore();
  const { loanRoutesConfig } = useLoanRoutes();


  useEffect(() => {
    invoicesDashboardStore.setTab(CounterPartyExpensesTab.PAYABLES_INVOICES_DASHBOARD);
    invoicesDashboardStore.setEntity(userStore?.userInformation?.companyId);
    invoicesDashboardStore.counterPartyExpenseGridStore.fetchGridData();
  }, []);

  const COLUMNS: GridColumnProps[] = [
    {
      field: "loanId",
      headerName: "Loan Id",
      width: 90,
      cellRenderer: "loanIdCellRenderer",
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: "invoice",
      headerName: "Invoice #",
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: "status",
      headerName: "Payable Status",
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: "feeType",
      headerName: "Bill Type",
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: "amount",
      headerName: "Bill Amount",
      minWidth: 125,
      cellRenderer: "currencyCellRenderer",
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: "createdDate",
      headerName: "Submission Date",
      minWidth: 125,
      cellRenderer: "dateCellRenderer",
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: "createdBy",
      headerName: "Submitted By",
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps(),
    },
  ];

  const onSortChanged = params => {
    const sortModel = params.api.getSortModel();
    invoicesDashboardStore.counterPartyExpenseGridStore.setSortModel(sortModel);
  };

  const onFilterChanged = params => {
    const filterModel = params.api.getFilterModel();
    invoicesDashboardStore.counterPartyExpenseGridStore.setFilterModel(filterModel);
  };

  const frameworkComponents = {
    loanIdCellRenderer: (params) => {
      return LinkColumn({
        ...params,
        url: isAttorneyPortal ? loanRoutesConfig.loans(params.value).children.foreclosureRequest.url
          : externalAgentLoanRoutesConfig.loans(params.value).children.foreclosureInvoice.url,
      });
    },
    currencyCellRenderer({ value }) {
      return formatCurrency(value);
    },
    dateCellRenderer({ value }) {
      if (!value) return "";
      const date = new Date(value);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
  };

  const toolbar = (
    < Tabs
      tabs={[{ label: "Invoices" }]}
      selectedTab={0}
      onTabChange={() => { }}
      right={
        <Button
          className={classes.exportButton}
          onClick={() => invoicesDashboardStore.exportServicingDashboardData()}
          testId="export"
          variant="contained"
          size="small"
          color="primary"
        >
          Export as Excel
        </Button>
      }
    />
  );

  return (
    <Layout>
      <DataGrid
        rows={invoicesDashboardStore.counterPartyExpenseGridStore.gridData.data.rows}
        toolbar={toolbar}
        frameworkComponents={frameworkComponents}
        columns={COLUMNS}
        headerCheckboxSelection={true}
        suppressRowClickSelection={true}
        onSortChanged={onSortChanged}
        onFilterChanged={onFilterChanged}
        isLoading={invoicesDashboardStore.counterPartyExpenseGridStore.isLoading}
        showFilters={invoicesDashboardStore.counterPartyExpenseGridStore.showFilters}
        sortModel={invoicesDashboardStore.counterPartyExpenseGridStore.sortModel}
        filterModel={invoicesDashboardStore.counterPartyExpenseGridStore.filterModel}
        paginationData={invoicesDashboardStore.counterPartyExpenseGridStore.gridData.meta}
        setPageNumber={invoicesDashboardStore.counterPartyExpenseGridStore.setPageNumber}
        key={CounterPartyExpensesTab.PAYABLES_INVOICES_DASHBOARD}
      />
    </Layout>
  );
});
